const DEFAULT_GEO_RESOLVE_URL = 'https://geo2.californiagaragerepair.com/resolve-url';
// Sent to geo2 so our landings are identifiable in its access log; the
// default here is node-fetch's own string, which reads like a stray bot.
const GEO_CLIENT_UA = 'LandingGeo/oasolutions (dandr)';
const LANDING_MONITOR_UA = /LandingMonitor\/oasolutions/i;
const REQUEST_TIMEOUT_MS = 500;
const DEBUG_GEO = process.env.GEO_DEBUG === '1';

export const GEO_RESOLVE_ENDPOINT = process.env.GEO_RESOLVE_URL || DEFAULT_GEO_RESOLVE_URL;

const normalizeCity = (payload) => {
  const city = payload?.match?.city;
  return typeof city === 'string' ? city.trim() : '';
};

const readHeader = (headers, name) => {
  if (!headers) {
    return '';
  }

  const normalizeValue = (value) => {
    if (Array.isArray(value)) {
      return value[0] || '';
    }
    return typeof value === 'string' ? value : '';
  };

  if (typeof headers.get === 'function') {
    return normalizeValue(headers.get(name));
  }

  return normalizeValue(headers[name]) || normalizeValue(headers[name.toLowerCase()]) || '';
};

const getRequestUrl = (context) => {
  const rawUrl = context?.url || '/';
  const isAbsolute = rawUrl.startsWith('http://') || rawUrl.startsWith('https://');
  const absoluteParsed = isAbsolute ? new URL(rawUrl) : null;
  const parsedPath = isAbsolute ? `${absoluteParsed.pathname}${absoluteParsed.search}` : rawUrl;
  const hasQuery = parsedPath.includes('?');
  const query =
    !hasQuery && context?.query && typeof context.query === 'object'
      ? new URLSearchParams(context.query).toString()
      : '';
  const path = query ? `${parsedPath}?${query}` : parsedPath;

  if (isAbsolute) {
    return `${absoluteParsed.protocol}//${absoluteParsed.host}${path}`;
  }

  const protocol = readHeader(context?.headers, 'x-forwarded-proto') || 'https';
  const host =
    readHeader(context?.headers, 'x-forwarded-host') ||
    readHeader(context?.headers, 'host') ||
    readHeader(context?.headers, ':authority') ||
    '';
  const siteUrlFallback = process.env.SITE_URL || process.env.URL || '';

  if (host) {
    return `${protocol}://${host}${path}`;
  }

  if (siteUrlFallback) {
    const parsed = new URL(siteUrlFallback);
    return `${parsed.protocol}//${parsed.host}${path}`;
  }

  return '';
};

export const resolveLocationFromUrl = async (fullUrl, { userIp = '', timeoutMs = REQUEST_TIMEOUT_MS } = {}) => {
  if (!fullUrl) {
    return '';
  }

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(GEO_RESOLVE_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'User-Agent': GEO_CLIENT_UA,
        ...(userIp && { 'X-Real-User-IP': userIp }),
      },
      body: JSON.stringify({ url: fullUrl }),
      signal: controller.signal,
    });

    if (!response.ok) {
      return '';
    }

    const payload = await response.json();
    return normalizeCity(payload);
  } catch (_) {
    return '';
  } finally {
    clearTimeout(timeout);
  }
};

export const getLocationServerData = async (context) => {
  // Our landing monitor never needs a city, so its probes never reach geo2.
  //
  // Flagging them with a header instead kept them out of geo2's statistics
  // but not off geo2: every probe was still a POST, and across the whole
  // fleet that is continuous background load - the same shape of traffic
  // that took geo2 down, only slower. Returning here uncouples the probe
  // frequency from geo2 entirely, which is what lets monitoring run often.
  const realUserAgent = readHeader(context?.headers, 'user-agent');
  if (LANDING_MONITOR_UA.test(realUserAgent)) {
    return { props: { location: '' } };
  }

  const fullUrl = getRequestUrl(context);
  if (!fullUrl) {
    if (DEBUG_GEO) {
      console.log('[geo] no request url in getServerData context');
    }
    return { props: { location: '' } };
  }

  const realUserIp =
    readHeader(context?.headers, 'cf-connecting-ip') ||
    (readHeader(context?.headers, 'x-forwarded-for') || '').split(',')[0].trim();

  const city = await resolveLocationFromUrl(fullUrl, { userIp: realUserIp });

  if (DEBUG_GEO) {
    console.log(`[geo] resolved city for ${fullUrl}: ${city || '(empty)'}`);
  }

  return { props: { location: city } };
};
